import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { apiGet, REMEMBERED_TENANT } from '@/api/client';
import { onSessionStarted } from '@/app/activity';
import { LoadingState } from '@/components/ui';
import { isLeaving, redirectToSuite } from './suiteSso';

/** Recargar porque ya hay sesión, a lo sumo una vez en este tiempo: si `/auth/me` falla con sesión viva, no se recarga en bucle. */
const RELOAD_KEY = 'pqrs.login.reloaded';
const RELOAD_GUARD_MS = 30_000;

/** La ruta pedida, solo si es de esta web (nunca otro origen ni `/login`). */
function safeVolver(volver: string | null): string {
  return volver && volver.startsWith('/') && !volver.startsWith('//') && !volver.startsWith('/\\') && !volver.startsWith('/login') ? volver : '/';
}

function reloadAllowed(): boolean {
  try {
    if (Date.now() - (Number(sessionStorage.getItem(RELOAD_KEY)) || 0) < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch { /* sin almacenamiento: se recarga */ }
  return true;
}

/**
 * `/login`: no es una pantalla (GUIA-AGENTES §3.1 y §3.7 de la suite, «Sin pantalla propia»): sin sesión se va de una al
 * ingreso de la suite (`suiteSso.ts`) y solo se ve `EstadoCargando` mientras el navegador carga la portada.
 * `?volver=<ruta>` es la ruta que la persona pidió (`RequireAuth`), y se la lleva. Aunque la sesión anterior haya
 * terminado (inactividad, vencimiento, cierre en otro equipo…), la de la suite vale: con sesión en el portal SGDEA se
 * reabre sin pedir nada. Tras «Salir» la pestaña ya va a terminar el SSO en la suite: aquí no se empieza otro ingreso.
 *
 * Varias pestañas (fila «Varias pestañas» de la guía): cuando la sesión termina, todas llegan aquí a la vez, pero solo la
 * que se ve va a la suite. Las demás esperan y, cuando otra pestaña abre sesión (`onSessionStarted`) o la persona vuelve
 * a mirarlas, preguntan si ya hay sesión (`/auth/session`): con ella recargan la ruta pedida; sin ella, la que se ve va
 * a ingresar. Así no salen varios ingresos a la vez («me pide ingresar una y otra vez»).
 */
export function LoginPage() {
  const [params] = useSearchParams();
  useEffect(() => {
    if (isLeaving()) return;
    const volver = params.get('volver');
    let done = false;
    let checking = false;
    let again = false;
    const decide = async () => {
      if (done || isLeaving()) return;
      if (checking) { again = true; return; }
      checking = true;
      // Sin sesión responde 401 (las rutas `/auth/` no avisan del 401 por su cuenta).
      const alive = await apiGet('/auth/session').then(() => true, () => false);
      checking = false;
      if (done || isLeaving()) return;
      if (alive && reloadAllowed()) {
        done = true;
        window.location.replace(safeVolver(volver));
      } else if (document.visibilityState === 'visible') {
        done = true;
        redirectToSuite({ tenant: REMEMBERED_TENANT, volver });
      } else if (again) {
        again = false;
        void decide();
      }
    };
    void decide();
    const onSeen = () => { if (document.visibilityState === 'visible') void decide(); };
    document.addEventListener('visibilitychange', onSeen);
    window.addEventListener('focus', onSeen);
    const stop = onSessionStarted(() => void decide());
    return () => {
      done = true;
      document.removeEventListener('visibilitychange', onSeen);
      window.removeEventListener('focus', onSeen);
      stop();
    };
    // Solo al montar: la decisión se toma una vez por pestaña.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div className="p-8"><LoadingState rows={4} /></div>;
}
